import type { MessageAction, RequestAction } from "trystero";
import { bindOnlineRecoveryActions } from "./online-recovery-actions.js";
import { certifyOnlineCheckpoint, recoverOnlineAuthority } from "./online-recovery-checkpoints.js";
import { acceptRecoveryHandoff, initiateRecoveryHandoff } from "./online-recovery-handoff.js";
import type { OnlineRecoveryView, RecoveryDescriptor } from "./online-room-types.js";
import {
  type CertifiedCheckpoint,
  type ElectionCertificate,
  RECOVERY_CHECKPOINT_INTERVAL_MS,
  RECOVERY_HEARTBEAT_TIMEOUT_MS,
  type RecoveryCheckpoint,
  RecoveryStateMachine,
} from "./recovery-protocol.js";
import { encodeRoomPacket } from "./room-packet.js";
import type { OnlineRoomAuthority } from "./room-protocol.js";
import type { RoomHello } from "./room-types.js";
import type { TrysteroRoomTransport } from "./trystero-room.js";
import type { ConflictConfig } from "./types.js";

interface RecoveryCallbacks {
  readonly config: () => ConflictConfig | undefined;
  readonly authority: () => OnlineRoomAuthority | undefined;
  readonly installAuthority: (authority: OnlineRoomAuthority) => void;
  readonly selectRemoteHost: (peerId: string) => void;
  readonly update: (view: OnlineRecoveryView, playing: boolean) => void;
}

export class OnlineRoomRecovery {
  readonly #transport: TrysteroRoomTransport;
  readonly #hello: RoomHello;
  readonly #callbacks: RecoveryCallbacks;
  readonly #peers = new Map<string, { readonly peerId: string; readonly hello: RoomHello }>();
  readonly #checkpointAction: RequestAction<string, string>;
  readonly #certificateAction: MessageAction<string>;
  readonly #heartbeatAction: MessageAction<string>;
  readonly #electionAction: MessageAction<string>;
  readonly #voteAction: MessageAction<string>;
  readonly #electedAction: MessageAction<string>;
  readonly #handoffAction: RequestAction<string, string>;
  readonly #timer: ReturnType<typeof setInterval>;
  #state: RecoveryStateMachine | undefined;
  #nextCheckpointAtMs = RECOVERY_CHECKPOINT_INTERVAL_MS;
  #electionPending = false;
  #electionStartedAtWallMs = 0;
  #lastPublishedCertificate: string | undefined;
  #currentElectionCertificate: ElectionCertificate | undefined;
  #authorityQuorumLost = false;
  #lastDisplayedGameMs = 0;

  constructor(transport: TrysteroRoomTransport, hello: RoomHello, callbacks: RecoveryCallbacks) {
    this.#transport = transport;
    this.#hello = hello;
    this.#callbacks = callbacks;
    this.#checkpointAction = transport.requestAction("recovery-checkpoint");
    this.#certificateAction = transport.action("recovery-certified");
    this.#heartbeatAction = transport.action("recovery-heartbeat");
    this.#electionAction = transport.action("recovery-election");
    this.#voteAction = transport.action("recovery-vote");
    this.#electedAction = transport.action("recovery-elected");
    this.#handoffAction = transport.requestAction("recovery-handoff");
    this.bindActions();
    this.#timer = setInterval(() => this.monitor(), 250);
  }

  initialize(descriptor: RecoveryDescriptor): void {
    if (this.#state) return;
    this.#state = new RecoveryStateMachine({
      selfId: this.#hello.sessionId,
      hostMemberId: descriptor.hostMemberId,
      voters: descriptor.voters,
      authorityEpoch: descriptor.authorityEpoch,
      membershipVersion: descriptor.membershipVersion,
      nowWallMs: Date.now(),
    });
    this.publishView(true, "Recovery checkpoint pending.");
  }

  descriptor(): RecoveryDescriptor | undefined {
    const status = this.#state?.status(Date.now());
    if (!status) return undefined;
    return Object.freeze({
      authorityEpoch: status.authorityEpoch,
      membershipVersion: status.membershipVersion,
      hostMemberId: status.hostMemberId,
      voters: status.voters,
    });
  }

  isCurrentHost(memberId: string): boolean {
    const descriptor = this.descriptor();
    if (descriptor) return descriptor.hostMemberId === memberId;
    return this.#peers.get(memberId)?.hello.role === "host";
  }

  peerJoined(peerId: string, hello: RoomHello): void {
    this.#peers.set(hello.sessionId, { peerId, hello });
    if (this.#currentElectionCertificate)
      void this.#electedAction.send(encodeRoomPacket(this.#currentElectionCertificate), {
        target: peerId,
      });
  }

  peerLeft(peerId: string): void {
    for (const [memberId, peer] of this.#peers) {
      if (peer.peerId === peerId) this.#peers.delete(memberId);
    }
    if (this.#callbacks.authority()) this.canAdvance();
  }

  observeState(authorityEpoch: number, gameTimeMs: number): boolean {
    const state = this.#state;
    if (!state || authorityEpoch !== state.status(Date.now()).authorityEpoch) return false;
    this.#lastDisplayedGameMs = gameTimeMs;
    return true;
  }

  acceptingOrders(): boolean {
    const state = this.#state;
    if (!state) return false;
    return this.#callbacks.authority() ? this.canAdvance() : !state.frozen(Date.now());
  }

  canAdvance(): boolean {
    const state = this.#state;
    if (!state) return false;
    const status = state.status(Date.now());
    const connected = this.connectedVoters().length;
    if (connected <= status.voters.length / 2) this.#authorityQuorumLost = true;
    if (this.#authorityQuorumLost)
      this.publishView(false, "Recovery voter majority lost; canonical progression is frozen.");
    return !this.#authorityQuorumLost;
  }

  async hostTick(gameTimeMs: number): Promise<void> {
    const state = this.#state;
    if (!state) return;
    const heartbeat = state.heartbeat(gameTimeMs);
    state.observeHeartbeat(heartbeat, Date.now());
    await this.#heartbeatAction.send(encodeRoomPacket(heartbeat));
    if (gameTimeMs < this.#nextCheckpointAtMs) return;
    this.#nextCheckpointAtMs =
      (Math.floor(gameTimeMs / RECOVERY_CHECKPOINT_INTERVAL_MS) + 1) *
      RECOVERY_CHECKPOINT_INTERVAL_MS;
    await this.publishCheckpoint();
  }

  dispose(): void {
    clearInterval(this.#timer);
  }

  latestCertified(): CertifiedCheckpoint {
    const certified = this.requiredState().latestCertified();
    if (!certified) throw new TypeError("No complete acknowledged room save is available yet");
    return certified;
  }

  availableCertified(): CertifiedCheckpoint | undefined {
    return this.#state?.latestCertified();
  }

  async handoff(nextHostMemberId: string): Promise<void> {
    const result = await initiateRecoveryHandoff(
      this.requiredState(),
      this.#callbacks.authority(),
      this.#peers.get(nextHostMemberId),
      nextHostMemberId,
      this.#handoffAction,
    );
    this.#currentElectionCertificate = result.certificate;
    this.#callbacks.selectRemoteHost(result.peerId);
    this.#authorityQuorumLost = false;
    this.publishRollback(result.gameTimeMs);
  }

  private bindActions(): void {
    bindOnlineRecoveryActions(
      {
        checkpoint: this.#checkpointAction,
        certificate: this.#certificateAction,
        heartbeat: this.#heartbeatAction,
        election: this.#electionAction,
        vote: this.#voteAction,
        elected: this.#electedAction,
        handoff: this.#handoffAction,
      },
      {
        state: () => this.requiredState(),
        requireHost: (peerId) => this.requireCurrentHost(peerId),
        memberId: (peerId) => this.memberId(peerId),
        heartbeat: (heartbeat) => {
          if (this.requiredState().observeHeartbeat(heartbeat, Date.now()))
            this.#lastDisplayedGameMs = Math.max(this.#lastDisplayedGameMs, heartbeat.gameTimeMs);
        },
        certified: () => this.publishView(true, "Certified recovery checkpoint available."),
        completeElection: (certificate) => this.completeElection(certificate),
        acceptElection: (certificate, peerId) => this.acceptElection(certificate, peerId),
        acceptHandoff: (raw, peerId) => this.acceptHandoff(raw, peerId),
      },
    );
  }

  private async acceptHandoff(raw: string, peerId: string): Promise<string> {
    try {
      this.requireCurrentHost(peerId);
      const result = acceptRecoveryHandoff(
        this.requiredState(),
        raw,
        this.#hello.sessionId,
        (checkpoint, epoch) => this.recoverAuthority(checkpoint, epoch),
      );
      this.#currentElectionCertificate = result.certificate;
      this.#authorityQuorumLost = false;
      this.#callbacks.installAuthority(result.authority);
      await this.#electedAction.send(encodeRoomPacket(result.certificate));
      this.publishRollback(result.gameTimeMs);
      return encodeRoomPacket({ ok: true });
    } catch (error) {
      return encodeRoomPacket({
        ok: false,
        reason: error instanceof Error ? error.message : "Host handoff failed",
      });
    }
  }

  private async publishCheckpoint(): Promise<void> {
    const state = this.requiredState();
    const authority = this.#callbacks.authority();
    const descriptor = this.descriptor();
    if (!authority || !descriptor) return;
    await certifyOnlineCheckpoint(
      state,
      authority,
      descriptor,
      [...this.#peers.values()],
      this.#checkpointAction,
      (certified) => this.publishCertificate(certified),
    );
  }

  private async publishCertificate(certified: CertifiedCheckpoint): Promise<void> {
    if (this.#lastPublishedCertificate === certified.checkpoint.id) return;
    this.#lastPublishedCertificate = certified.checkpoint.id;
    await this.#certificateAction.send(encodeRoomPacket(certified));
    this.publishView(true, "Certified recovery checkpoint available.");
  }

  private monitor(): void {
    const state = this.#state;
    if (!state || this.#callbacks.authority() || !state.frozen(Date.now())) return;
    this.publishView(false, "Host heartbeat lost; canonical progression and orders are frozen.");
    const nowWallMs = Date.now();
    if (
      this.#electionPending &&
      nowWallMs - this.#electionStartedAtWallMs < RECOVERY_HEARTBEAT_TIMEOUT_MS
    )
      return;
    this.#electionPending = false;
    const request = state.beginElection(nowWallMs, this.connectedVoters());
    if (!request) return;
    this.#electionPending = true;
    this.#electionStartedAtWallMs = nowWallMs;
    const own = state.vote(request);
    if (own) state.recordElectionVote(own);
    void this.#electionAction.send(encodeRoomPacket(request));
  }

  private async completeElection(certificate: ElectionCertificate): Promise<void> {
    const bundle = this.requiredState().installElection(certificate, Date.now());
    const authority = this.recoverAuthority(bundle.checkpoint, certificate.authorityEpoch);
    this.#callbacks.installAuthority(authority);
    this.#authorityQuorumLost = false;
    this.#currentElectionCertificate = certificate;
    await this.#electedAction.send(encodeRoomPacket(certificate));
    this.publishRollback(bundle.checkpoint.gameTimeMs);
  }

  private acceptElection(certificate: ElectionCertificate, hostPeerId: string): void {
    if ((this.descriptor()?.authorityEpoch ?? 0) >= certificate.authorityEpoch) return;
    const bundle = this.requiredState().installElection(certificate, Date.now());
    this.#currentElectionCertificate = certificate;
    this.#authorityQuorumLost = false;
    this.#callbacks.selectRemoteHost(hostPeerId);
    this.#electionPending = false;
    this.#electionStartedAtWallMs = 0;
    this.publishRollback(bundle.checkpoint.gameTimeMs);
  }

  private publishRollback(restoredGameMs: number): void {
    this.#callbacks.update(
      {
        ...(this.descriptor() as RecoveryDescriptor),
        frozen: false,
        message: `Authority recovered at ${restoredGameMs}ms; uncertified work was rolled back.`,
        rollbackFromGameMs: this.#lastDisplayedGameMs,
        restoredGameMs,
      },
      true,
    );
  }

  private publishView(playing: boolean, message: string): void {
    const descriptor = this.descriptor();
    if (!descriptor) return;
    this.#callbacks.update(
      { ...descriptor, frozen: this.requiredState().frozen(Date.now()), message },
      playing,
    );
  }

  private connectedVoters(): readonly string[] {
    const voters = this.requiredState().status(Date.now()).voters;
    return [this.#hello.sessionId, ...this.#peers.keys()]
      .filter((memberId) => voters.includes(memberId))
      .sort();
  }

  private requireCurrentHost(peerId: string): void {
    const state = this.requiredState().status(Date.now());
    if (this.memberId(peerId) !== state.hostMemberId)
      throw new TypeError("Recovery packet did not come from the current host");
  }

  private memberId(peerId: string): string | undefined {
    return [...this.#peers.entries()].find(([, peer]) => peer.peerId === peerId)?.[0];
  }

  private requiredState(): RecoveryStateMachine {
    if (!this.#state) throw new TypeError("Room recovery is not initialized");
    return this.#state;
  }

  private recoverAuthority(
    checkpoint: RecoveryCheckpoint,
    authorityEpoch: number,
  ): OnlineRoomAuthority {
    return recoverOnlineAuthority(
      this.#callbacks.config(),
      this.#transport.selfId,
      this.#hello,
      checkpoint,
      authorityEpoch,
      [...this.#peers.values()],
    );
  }
}
